import React, { useEffect, useState } from 'react';
import { Settings, HelpCircle, Heart, Calendar, CreditCard, LogOut, Edit2, Check, Plus, Trash2, ShoppingBag, Gift } from 'lucide-react';
import { UserProfile, SpecialDate } from '../../types';
import { MOCK_DATES, MOCK_GIFTS } from '../../data/mockData';
import { AccordionSection } from '../AccordionSection';
import { QRCodeDisplay } from '../QRCodeDisplay';
import { SpecialDateItem } from '../SpecialDateItem';
import { Button } from '../ui/Button';
import { useToast } from '../ToastContext';
import { triggerConfetti } from '../ui/ConfettiButton';
interface ProfileScreenProps {
  user: UserProfile;
  onNavigate?: (screen: string) => void;
  openSection?: string | null;
}
export function ProfileScreen({
  user,
  onNavigate,
  openSection
}: ProfileScreenProps) {
  const [dates, setDates] = useState<SpecialDate[]>(MOCK_DATES);
  const [birthday, setBirthday] = useState(user.birthday || '');
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [birthdayInput, setBirthdayInput] = useState(birthday);
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState({
    name: '',
    date: '',
    type: 'other' as const,
    notes: ''
  });
  const {
    showToast
  } = useToast();
  const handleSaveBirthday = () => {
    if (!birthdayInput.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    setBirthday(birthdayInput);
    setIsEditingBirthday(false);
    localStorage.setItem('userBirthday', birthdayInput);
    triggerConfetti();
    showToast('Birthday saved! 🎉', 'success');
  };
  const handleDeleteDate = (id: string) => {
    setDates(prev => prev.filter(d => d.id !== id));
    showToast('Date removed', 'info');
  };
  const handleAddDate = () => {
    if (!newDate.name || !newDate.date) {
      showToast('Please fill in name and date', 'error');
      return;
    }
    if (!newDate.date.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    const date: SpecialDate = {
      id: `d-${Date.now()}`,
      ...newDate
    };
    setDates(prev => [...prev, date]);
    setNewDate({
      name: '',
      date: '',
      type: 'other',
      notes: ''
    });
    setShowAddDate(false);
    showToast('Date added successfully', 'success');
  };
  const formatBirthday = (dateStr: string) => {
    if (!dateStr) return 'Not set';
    const [month, day] = dateStr.split('-');
    const date = new Date(2000, parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  };
  // Mock orders data
  const recentOrders = [{
    id: '1',
    storeName: 'Blue Tokai Coffee',
    date: 'Today, 9:15 AM',
    total: 120,
    items: 'Filter Coffee'
  }, {
    id: '2',
    storeName: 'Cafe Madras',
    date: 'Yesterday',
    total: 180,
    items: 'Paneer Sandwich'
  }, {
    id: '3',
    storeName: 'Chai Point',
    date: '2 days ago',
    total: 90,
    items: 'Masala Chai + Samosa'
  }];
  // Get user's received gifts
  const receivedGifts = MOCK_GIFTS.filter(g => g.recipient === 'You');
  const pendingGifts = receivedGifts.filter(g => g.status === 'pending');
  const formatGiftDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500" data-id="element-1070">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8" data-id="element-1071">
        <div className="w-20 h-20 rounded-full bg-gray-200 border-4 border-white shadow-md overflow-hidden" data-id="element-1072">
          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" data-id="element-1073" />
        </div>
        <div data-id="element-1074">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1075">{user.name}</h1>
          <p className="text-gray-500" data-id="element-1076">{user.email}</p>
          <div className="flex items-center gap-2 mt-2" data-id="element-1077">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full" data-id="element-1078">
              {user.points} Points
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2" data-id="element-1079">
        <AccordionSection title="Your User ID" icon={<div className="w-5 h-5" data-id="element-1081" />} data-id="element-1080">
          <div className="flex justify-center pb-4" data-id="element-1082">
            <QRCodeDisplay value={user.id} avatar={user.avatar} data-id="element-1083" />
          </div>
          <p className="text-xs text-center text-gray-500 mt-2" data-id="element-1084">
            Let friends scan to send you gifts
          </p>
        </AccordionSection>

        <AccordionSection title="Special Dates" icon={<Calendar size={20} data-id="element-1086" />} data-id="element-1085">
          <div className="pb-4 space-y-4" data-id="element-1087">
            {/* My Birthday Section */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100" data-id="element-1088">
              <div className="flex items-center justify-between mb-3" data-id="element-1089">
                <div className="flex items-center gap-2" data-id="element-1090">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-1091">
                    <Calendar size={16} className="text-purple-600" data-id="element-1092" />
                  </div>
                  <span className="font-bold text-gray-900" data-id="element-1093">My Birthday</span>
                </div>
                {!isEditingBirthday && <button onClick={() => {
                setIsEditingBirthday(true);
                setBirthdayInput(birthday);
              }} className="p-1.5 bg-white rounded-lg hover:bg-purple-100 transition-colors" data-id="element-1094">
                    <Edit2 size={14} className="text-purple-600" data-id="element-1095" />
                  </button>}
              </div>

              {isEditingBirthday ? <div className="flex items-center gap-2" data-id="element-1096">
                  <input type="text" placeholder="MM-DD" value={birthdayInput} onChange={e => setBirthdayInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-1097" />
                  <button onClick={handleSaveBirthday} className="p-2 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-1098">
                    <Check size={16} className="text-gray-900" strokeWidth={3} data-id="element-1099" />
                  </button>
                  <button onClick={() => setIsEditingBirthday(false)} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1100">
                    <Trash2 size={16} className="text-gray-600" data-id="element-1101" />
                  </button>
                </div> : <p className="text-gray-700 font-medium" data-id="element-1102">
                  {formatBirthday(birthday)}
                </p>}
            </div>

            {/* Important Dates */}
            <div data-id="element-1103">
              <div className="flex items-center justify-between mb-3" data-id="element-1104">
                <p className="text-sm font-bold text-gray-500 uppercase" data-id="element-1105">
                  Important Dates
                </p>
                <button onClick={() => setShowAddDate(!showAddDate)} className="p-1.5 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-1106">
                  <Plus size={14} className="text-gray-900" strokeWidth={3} data-id="element-1107" />
                </button>
              </div>

              {showAddDate && <div className="bg-gray-50 rounded-xl p-4 mb-3 space-y-3 border border-gray-200" data-id="element-1108">
                  <input type="text" placeholder="Occasion name" value={newDate.name} onChange={e => setNewDate({
                ...newDate,
                name: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-1109" />
                  <input type="text" placeholder="MM-DD" value={newDate.date} onChange={e => setNewDate({
                ...newDate,
                date: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-1110" />
                  <select value={newDate.type} onChange={e => setNewDate({
                ...newDate,
                type: e.target.value as any
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-1111">
                    <option value="birthday" data-id="element-1112">Birthday</option>
                    <option value="anniversary" data-id="element-1113">Anniversary</option>
                    <option value="holiday" data-id="element-1114">Holiday</option>
                    <option value="other" data-id="element-1115">Other</option>
                  </select>
                  <textarea placeholder="Notes (optional)" value={newDate.notes} onChange={e => setNewDate({
                ...newDate,
                notes: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm h-20 resize-none" data-id="element-1116" />
                  <div className="flex gap-2" data-id="element-1117">
                    <Button size="sm" onClick={handleAddDate} className="flex-1" data-id="element-1118">
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setShowAddDate(false)} className="flex-1" data-id="element-1119">
                      Cancel
                    </Button>
                  </div>
                </div>}

              {dates.length > 0 ? dates.map(date => <SpecialDateItem key={date.id} date={date} onDelete={handleDeleteDate} data-id="element-1120" />) : <div className="text-center py-8 text-gray-400" data-id="element-1121">
                  <Calendar size={32} className="mx-auto mb-2 opacity-50" data-id="element-1122" />
                  <p className="text-sm" data-id="element-1123">No dates added yet</p>
                  <button onClick={() => setShowAddDate(true)} className="mt-2 text-sm text-purple-600 font-medium" data-id="element-1124">
                    Add Your First Date
                  </button>
                </div>}
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Your Rewards" icon={<div className="w-5 h-5 bg-purple-100 rounded-full" data-id="element-1126" />} data-id="element-1125">
          <div className="space-y-3 pb-2" data-id="element-1127">
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-1128">
              <div className="flex justify-between items-center mb-2" data-id="element-1129">
                <span className="font-medium text-sm" data-id="element-1130">Bean & Leaf</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-1131">
                  7/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1132">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '70%'
              }} data-id="element-1133" />
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-1134">
              <div className="flex justify-between items-center mb-2" data-id="element-1135">
                <span className="font-medium text-sm" data-id="element-1136">Urban Garden</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-1137">
                  3/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1138">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '30%'
              }} data-id="element-1139" />
              </div>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Saved Cards" icon={<CreditCard size={20} data-id="element-1141" />} data-id="element-1140">
          <div className="p-4 bg-gray-50 rounded-xl mb-2 flex items-center gap-3" data-id="element-1142">
            <div className="w-10 h-6 bg-gray-800 rounded" data-id="element-1143" />
            <span className="font-medium text-sm" data-id="element-1144">•••• 4242</span>
            <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold" data-id="element-1145">
              Default
            </span>
          </div>
        </AccordionSection>

        <AccordionSection title="Favorite Places" icon={<Heart size={20} data-id="element-1147" />} data-id="element-1146">
          <p className="text-sm text-gray-500 p-2" data-id="element-1148">3 saved places</p>
        </AccordionSection>

        <AccordionSection title="Orders" icon={<ShoppingBag size={20} data-id="element-1150" />} data-id="element-1149">
          <div className="space-y-2 pb-2" data-id="element-1151">
            {recentOrders.map(order => <button key={order.id} onClick={() => onNavigate?.('orders')} className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-1152">
                <div className="flex items-center justify-between mb-1" data-id="element-1153">
                  <span className="font-medium text-sm" data-id="element-1154">{order.items}</span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-1155">
                    ₹{order.total}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1156">
                  <span className="text-xs text-gray-500" data-id="element-1157">
                    {order.storeName}
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-1158">{order.date}</span>
                </div>
              </button>)}
            <button onClick={() => onNavigate?.('orders')} className="w-full text-center py-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors" data-id="element-1159">
              View All Orders
            </button>
          </div>
        </AccordionSection>

        <AccordionSection title="Gift Wallet" icon={<Gift size={20} data-id="element-1161" />} defaultOpen={openSection === 'giftWallet'} data-id="element-1160">
          <div className="space-y-2 pb-2" data-id="element-1162">
            {receivedGifts.length > 0 ? <>
                {receivedGifts.slice(0, 3).map(gift => <button key={gift.id} onClick={() => onNavigate?.('giftHistory')} className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-id="element-1163">
                    <div className="flex items-center gap-3" data-id="element-1164">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-1165">
                        <img src={gift.itemImage} alt={gift.itemName} className="w-full h-full object-cover" data-id="element-1166" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1167">
                        <div className="flex items-center justify-between mb-1" data-id="element-1168">
                          <span className="font-medium text-sm truncate" data-id="element-1169">
                            {gift.itemName}
                          </span>
                          {gift.status === 'pending' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold flex-shrink-0 ml-2" data-id="element-1170">
                              New
                            </span>}
                        </div>
                        <div className="flex items-center justify-between" data-id="element-1171">
                          <span className="text-xs text-gray-500 truncate" data-id="element-1172">
                            From {gift.sender}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2" data-id="element-1173">
                            {formatGiftDate(gift.date)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>)}
                <button onClick={() => onNavigate?.('giftHistory')} className="w-full text-center py-2 text-sm text-purple-600 font-medium hover:text-purple-700 transition-colors" data-id="element-1174">
                  View All Gifts
                  {pendingGifts.length > 0 && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold" data-id="element-1175">
                      {pendingGifts.length} pending
                    </span>}
                </button>
              </> : <div className="text-center py-6 text-gray-400" data-id="element-1176">
                <Gift size={32} className="mx-auto mb-2 opacity-50" data-id="element-1177" />
                <p className="text-sm" data-id="element-1178">No gifts yet</p>
              </div>}
          </div>
        </AccordionSection>

        <AccordionSection title="Help & Support" icon={<HelpCircle size={20} data-id="element-1180" />} data-id="element-1179">
          <div className="space-y-2 pb-2" data-id="element-1181">
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-1182">
              FAQ
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-1183">
              Contact Support
            </button>
          </div>
        </AccordionSection>

        <div className="pt-6" data-id="element-1184">
          <button className="flex items-center gap-3 text-red-500 font-medium px-1 hover:text-red-600 transition-colors" data-id="element-1185">
            <LogOut size={20} data-id="element-1186" /> Sign Out
          </button>
        </div>
      </div>
    </div>;
}